/**
 * Distribución de las columnas del footer.
 *
 * Antes el grid estaba clavado (`lg:grid-cols-4` en el clásico, spans de 2 sobre
 * 12 en el moderno) sin importar cuántas columnas de enlaces tuviera el footer:
 * con 2 columnas quedaba un hueco enorme a la derecha, y con 5 se desbordaba.
 * Acá el layout se calcula según cuántas columnas hay, y el cliente puede
 * forzar un número fijo desde el panel si prefiere otra distribución.
 *
 * Las clases se devuelven literales (no interpoladas) porque Tailwind solo
 * detecta las que aparecen escritas completas en el código.
 */

/** 'auto' = se ajusta solo al número de columnas que tenga el footer. */
export type ColumnasPorFila = 'auto' | '2' | '3' | '4' | '5';

const entre = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

/** Cuántas columnas de enlaces se reparten por fila. */
const columnasEfectivas = (visibles: number, preferencia?: ColumnasPorFila): number => {
  if (preferencia && preferencia !== 'auto') return entre(parseInt(preferencia, 10), 1, 5);
  return entre(visibles, 1, 5);
};

/**
 * Footer clásico: el logo ocupa una celda más, así que el grid lleva N+1.
 *
 * `extra` son celdas que NO son columnas de enlaces y por eso no pasan por la
 * preferencia del cliente (hoy: la columna de redes sociales). Va aparte porque
 * `columnasEfectivas` IGNORA `visibles` cuando hay una preferencia fija: sumarle
 * la columna de redes al primer parámetro no movía el grid ni un poco, y la
 * columna terminaba cayendo sola a una segunda fila debajo del logo.
 */
export const gridFooterClasico = (
  visibles: number,
  preferencia?: ColumnasPorFila,
  extra = 0
): string => {
  const total = entre(columnasEfectivas(visibles, preferencia) + extra + 1, 2, 6);
  switch (total) {
    case 2: return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 lg:gap-12';
    case 3: return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12';
    case 5: return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12';
    case 6: return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-12';
    default: return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12';
  }
};

/**
 * Footer moderno: grid de 12 donde el logo va más ancho. Los repartos están
 * elegidos para sumar 12 exactos y no dejar huecos.
 */
export const spansFooterModerno = (
  visibles: number,
  preferencia?: ColumnasPorFila
): { logo: string; columna: string } => {
  switch (columnasEfectivas(visibles, preferencia)) {
    case 1: return { logo: 'lg:col-span-4', columna: 'lg:col-span-8' };
    case 2: return { logo: 'lg:col-span-4', columna: 'lg:col-span-4' };
    case 3: return { logo: 'lg:col-span-3', columna: 'lg:col-span-3' };
    case 5: return { logo: 'lg:col-span-2', columna: 'lg:col-span-2' };
    default: return { logo: 'lg:col-span-4', columna: 'lg:col-span-2' };
  }
};
